/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import React, { useEffect, useRef } from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';

type ButtonProps = React.PropsWithChildren<
  {
    color?: MuiButtonProps['color'] | 'urgent';
    'data-testid'?: string;
    disabled?: boolean;
    focus?: boolean;
    variant?: 'contained' | 'text';
  } & Pick<
    MuiButtonProps,
    'className' | 'endIcon' | 'startIcon' | 'type' | 'onClick' | 'onKeyPress'
  >
>;

export const Button = ({
  className,
  children,
  color = 'primary',
  endIcon,
  'data-testid': dataTestid,
  disabled,
  focus,
  onClick,
  onKeyPress,
  type,
  variant = 'contained',
  startIcon,
}: ButtonProps) => {
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focus) btnRef?.current?.focus();
  }, [focus]);

  // Handle urgent color custom styling
  const isUrgentColor = color === 'urgent';
  const buttonColor = isUrgentColor ? 'primary' : (color as MuiButtonProps['color']);
  const extraStyles = isUrgentColor ? urgentStyles : '';

  return (
    <MuiButton
      className={className}
      css={css`
        ${TypographyStyles.button}
        border-radius: 0;
        padding: ${Spacing.Unit2}px ${Spacing.Unit3}px;
        line-height: 17.04px;
        ${extraStyles}
      `}
      disableElevation
      data-testid={dataTestid || 'nds-component-button'}
      disabled={disabled}
      onClick={onClick}
      onKeyPress={onKeyPress}
      ref={btnRef}
      variant={variant}
      type={type}
      color={buttonColor}
      startIcon={startIcon}
      endIcon={endIcon}
    >
      {children}
    </MuiButton>
  );
};

const urgentStyles = css`
  &.MuiButton-contained {
    background-color: ${Colors.Peach5};
    color: ${Colors.White};

    &:hover {
      background-color: ${Colors.Peach6};
    }

    &:disabled {
      background-color: ${Colors.Peach2};
      color: ${Colors.Gray4};
    }
  }

  &.MuiButton-text {
    color: ${Colors.Peach5};

    &:hover {
      color: ${Colors.Peach6};
      background-color: ${Colors.Peach1};
    }

    &:disabled {
      color: ${Colors.Gray4};
    }
  }
`;
